import { type SortingState, type Table } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, Search, X } from "lucide-react";

import { Button } from "~/shared/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/shared/ui/select";
import {
    DataTableFilterButton,
    DataTableFilterPanel,
    type FilterFieldConfig,
} from "~/shared/ui/data-table/DataTableFilter";
import { DataTableColumnToggle } from "~/shared/ui/data-table/DataTableColumnToggle";

import { SECTION_V5 } from "./design-tokens";
import { columnLabel } from "./section-columns";
import { SectionViewSwitcher, type SectionView } from "./SectionViewSwitcher";

interface DebouncedSearch {
    value: string;
    onChange: (value: string) => void;
    clear: () => void;
}

interface SectionToolbarProps<TRow extends { id: string }> {
    table: Table<TRow>;
    search: DebouncedSearch;
    /** Render the search input. Default true; false hides it (backend without text search). */
    searchable?: boolean;
    effectiveView: SectionView;
    view: SectionView;
    onViewChange: (view: SectionView) => void;
    showGrid: boolean;
    sorting: SortingState;
    onSortingChange: (s: SortingState) => void;
    filterFields: FilterFieldConfig[];
    filterValues: Record<string, unknown>;
    onFilterChange: (key: string, value: unknown) => void;
    onFilterApply: () => void;
    onFilterReset: () => void;
    filterOpen: boolean;
    onFilterOpenChange: (open: boolean) => void;
    searchPlaceholder?: string;
    clearSearchLabel?: string;
    filterLabel?: string;
    applyLabel?: string;
    resetLabel?: string;
    columnsLabel?: string;
    sortLabel?: string;
    toggleSortDirLabel?: string;
}

/** Controls toolbar for `SectionDataView`: search, sort, filter toggle, column toggle, view switcher. */
export function SectionToolbar<TRow extends { id: string }>({
    table,
    search,
    searchable = true,
    effectiveView,
    view,
    onViewChange,
    showGrid,
    sorting,
    onSortingChange,
    filterFields,
    filterValues,
    onFilterChange,
    onFilterApply,
    onFilterReset,
    filterOpen,
    onFilterOpenChange,
    searchPlaceholder,
    clearSearchLabel,
    filterLabel,
    applyLabel,
    resetLabel,
    columnsLabel,
    sortLabel,
    toggleSortDirLabel,
}: SectionToolbarProps<TRow>) {
    const sortableColumns = table.getAllColumns().filter((c) => c.getCanSort());
    const activeSort = sorting[0];

    return (
        <div className="flex flex-col gap-2 px-4 pt-2 md:px-8">
            <div className={SECTION_V5.toolbarRow}>
                {searchable ? (
                    <div className={SECTION_V5.searchWrapper}>
                        <Search
                            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground/60"
                            aria-hidden
                        />
                        <input
                            type="search"
                            value={search.value}
                            onChange={(e) => search.onChange(e.target.value)}
                            placeholder={searchPlaceholder}
                            aria-label={searchPlaceholder}
                            className={SECTION_V5.searchInput}
                        />
                        {search.value ? (
                            <button
                                type="button"
                                onClick={search.clear}
                                aria-label={clearSearchLabel}
                                className="absolute top-1/2 right-2.5 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
                            >
                                <X className="size-3.5" aria-hidden />
                            </button>
                        ) : null}
                    </div>
                ) : (
                    <div className="min-w-0 flex-1" />
                )}

                {effectiveView !== "table" && sortableColumns.length > 0 ? (
                    <div className="flex shrink-0 items-center gap-1">
                        <Select
                            value={activeSort?.id ?? ""}
                            onValueChange={(id) => onSortingChange([{ id, desc: activeSort?.desc ?? false }])}
                        >
                            <SelectTrigger className="w-[150px]">
                                <SelectValue placeholder={sortLabel} />
                            </SelectTrigger>
                            <SelectContent position="popper" side="bottom" avoidCollisions={false}>
                                {sortableColumns.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {columnLabel(c)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            variant="outline"
                            size="icon"
                            className="size-9 shrink-0"
                            disabled={!activeSort}
                            aria-label={toggleSortDirLabel}
                            onClick={() =>
                                activeSort && onSortingChange([{ id: activeSort.id, desc: !activeSort.desc }])
                            }
                        >
                            {activeSort?.desc ? (
                                <ArrowDown className="size-4" aria-hidden />
                            ) : (
                                <ArrowUp className="size-4" aria-hidden />
                            )}
                        </Button>
                    </div>
                ) : null}

                <div className={SECTION_V5.toolbarActions}>
                    <DataTableFilterButton
                        isOpen={filterOpen}
                        onToggle={() => onFilterOpenChange(!filterOpen)}
                        label={filterLabel}
                    />
                    <div className="hidden items-center gap-2 md:flex">
                        {effectiveView === "table" ? (
                            <DataTableColumnToggle table={table} label={columnsLabel} />
                        ) : null}
                        <SectionViewSwitcher value={view} onChange={onViewChange} showGrid={showGrid} />
                    </div>
                </div>
            </div>

            <DataTableFilterPanel
                fields={filterFields}
                values={filterValues}
                onChange={onFilterChange}
                onApply={onFilterApply}
                onReset={onFilterReset}
                isOpen={filterOpen}
                applyLabel={applyLabel}
                resetLabel={resetLabel}
            />
        </div>
    );
}
